/**
 * WarRoom.jsx · v3.5.0 · War Room Unificado
 * ═══════════════════════════════════════════════════════════════════
 * Reemplaza `MapaGIS`, `CalendarioTecnicos` y `MapaOTsDia` en una sola
 * vista táctica a pantalla completa para el dispatcher:
 *   • Mapa Leaflet full-screen
 *   • Smart Pins POR TÉCNICO (badge # OTs + halo % ocupación)
 *   • Click en pin → Sidebar deslizante con:
 *       KM totales, mini-calendario cronológico, ruta polyline
 *   • Navegación de día (prev / hoy / next)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapContainer, TileLayer, Marker, Popup, Polyline, useMap, CircleMarker, Polygon,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Clock, Loader2,
  MapPin, Navigation as NavIcon, Route as RouteIcon, RefreshCcw,
  X, Users, Gauge, Milestone, LayoutGrid, Maximize2, Radio, CheckCircle2,
  Search as SearchIcon, Palette,
} from "lucide-react";
import api from "../lib/api";
import { useGlobalFilters } from "../lib/globalFilters";

// v93.2.0 · Estado del GPS del técnico (último ping)
const GPS_COLORS = { live: "#16A34A", stale: "#F59E0B", offline: "#DC2626" };
const gpsColor = (p) => GPS_COLORS[p?.gps_status] || "#9CA3AF";
const gpsLabel = (p) => {
  if (!p) return "Sin GPS (sin app o sin señal en 7 días)";
  const m = p.minutes_since ?? 0;
  const hora = p.captured_at ? new Date(p.captured_at).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" }) : "";
  const hace = m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${Math.floor(m / 1440)} día(s)`;
  return `${p.gps_status === "live" ? "En línea" : "Última señal"} · hace ${hace}${hora ? ` (${hora})` : ""}`;
};

// v98.8.0 · Colores reservados para bloqueos (NO usar en sucursales/zonas)
const BLOCK_COLORS = { polygon: "#DC2626", branch: "#FACC15", tech: "#39FF14" };

// v98.8.0 · Estado del técnico (determina icono)
const TRAVELING_STATES = ["en_camino", "traveling", "asignada", "assigned"];
const ON_SITE_STATES = ["en_sitio", "on_site", "en_ejecucion", "in_progress", "confirmada"];

// v98.8.0 · Icono dinámico: camioneta (viajando) o persona (en sitio), coloreado por sucursal/zona
const buildDynamicTechPin = (tech, selected, techColor) => {
  const color = techColor || "#64748B";
  const isTraveling = (tech.stops || []).some(s => TRAVELING_STATES.includes((s.internal_status || "").toLowerCase()) && !ON_SITE_STATES.includes((s.internal_status || "").toLowerCase()));
  const isOnSite = (tech.stops || []).some(s => ON_SITE_STATES.includes((s.internal_status || "").toLowerCase()));
  // SVG camioneta estilo Waze vs persona estilo Rappi
  const truckSvg = `<svg viewBox="0 0 24 24" fill="white" width="18" height="18"><path d="M18 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm1.5-9H17V12h4.46L19.5 9.5zM6 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM20 8l3 4v5h-2a3 3 0 01-6 0H9a3 3 0 01-6 0H1V6a2 2 0 012-2h13v4h4z"/></svg>`;
  const personSvg = `<svg viewBox="0 0 24 24" fill="white" width="18" height="18"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;
  const icon = isOnSite ? personSvg : truckSvg;
  const initials = (tech.tech_name || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
  const ring = selected ? "border:3px solid #E0E621;box-shadow:0 0 0 5px #E0E62155,0 6px 16px rgba(0,0,0,.45);" : "border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,.35);";
  return L.divIcon({
    className: "",
    html: `<div style="position:relative;width:54px;height:66px;">
      <div style="position:absolute;top:0;left:0;width:48px;height:48px;background:${color};border-radius:50%;${ring}display:flex;flex-direction:column;align-items:center;justify-content:center;">
        <div style="margin-top:2px">${icon}</div>
      </div>
      <div style="position:absolute;top:-6px;right:-6px;min-width:20px;height:20px;padding:0 4px;background:#0A3747;color:#E0E621;border-radius:10px;border:2px solid #fff;font-size:10px;font-weight:900;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;box-shadow:0 2px 4px rgba(0,0,0,.3)">${tech.ots_count}</div>
      <div style="position:absolute;top:36px;left:2px;max-width:50px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:rgba(0,0,0,.7);color:#fff;border-radius:4px;padding:1px 4px;font-size:8px;font-weight:700;font-family:system-ui,sans-serif;text-align:center">${initials}</div>
    </div>`,
    iconSize: [54, 66],
    iconAnchor: [24, 48],
    popupAnchor: [3, -36],
  });
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// Color del pin según % ocupación (verde<50, amarillo<80, rojo>=80)
const occupationColor = (pct) => {
  if (pct == null) return "#64748B";
  if (pct >= 90) return "#B91C1C";
  if (pct >= 70) return "#D97706";
  if (pct >= 40) return "#059669";
  return "#0EA5E9";
};

// v98.8.0 · DEPRECATED: buildTechPin reemplazado por buildDynamicTechPin
// se mantiene como fallback si branch_color no disponible
const buildTechPin = (tech, selected, techColor) => buildDynamicTechPin(tech, selected, techColor || occupationColor(tech.occupation_pct));

function FitBoundsOnce({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lon], 12); return; }
    const b = L.latLngBounds(points.map(p => [p.lat, p.lon]));
    map.fitBounds(b, { padding: [80, 80], maxZoom: 12 });
  }, [points, map]);
  return null;
}

// v98.8.0 · Leyenda del mapa limpia
function MapControls({ showTechs, setShowTechs, showOts, setShowOts, compact }) {
  return (
    <div className={`absolute ${compact ? "bottom-2 left-2" : "bottom-3 left-3"} z-[500] bg-white/95 border border-gray-200 rounded-lg px-3 py-2 text-[10px] shadow-lg space-y-0.5 min-w-[170px]`}
      data-testid="warroom-map-controls">
      <div className="font-bold text-[#0A3747] mb-1">Capas</div>
      <label className="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={showTechs} onChange={e => setShowTechs(e.target.checked)} data-testid="layer-techs" /> Técnicos
      </label>
      <label className="flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={showOts} onChange={e => setShowOts(e.target.checked)} data-testid="layer-ots" /> OTs
      </label>
      <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Iconos</div>
      <div className="flex items-center gap-1"><span className="text-[9px]">🚐</span> Viajando</div>
      <div className="flex items-center gap-1"><span className="text-[9px]">👤</span> En sitio</div>
      <div className="font-bold text-[#0A3747] mt-1.5 mb-0.5">Bloqueos</div>
      <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded" style={{ background: "#DC2626" }} /> Zona</div>
      <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded" style={{ background: "#FACC15" }} /> Sucursal</div>
      <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded" style={{ background: "#39FF14" }} /> Técnico</div>
    </div>
  );
}



function FocusHandler({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && target.lat != null && target.lon != null) {
      map.flyTo([target.lat, target.lon], 13, { duration: 0.6 });
    }
  }, [target, map]);
  return null;
}

export default function WarRoom() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [fleet, setFleet] = useState([]);              // v93.3.0 · todos los técnicos
  const [fleetCounts, setFleetCounts] = useState({});
  const [showTechs, setShowTechs] = useState(true);
  const [showOts, setShowOts] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedTechId, setSelectedTechId] = useState(null);
  const [layoutMode, setLayoutMode] = useState("2x2");
  const [techFilter, setTechFilter] = useState("");
  const [tick, setTick] = useState(0);
  // v9.0 · Conectar filtros globales unificados
  const { filters, setFilter, asParams } = useGlobalFilters();
  const branchId = filters.branch_office_id || "";
  const zoneId = filters.zone_id || "";
  const otSearch = filters.q || "";
  const statusFilter = filters.internal_status || "";
  const techFilterGlobal = filters.technician_id || "";
  const [branches, setBranches] = useState([]);
  const [layers, setLayers] = useState({ events: [], blocked_zones: [] });
  // v98.8.0 · War Room rediseño
  const [showTechWizard, setShowTechWizard] = useState(null);
  const [techWizardData, setTechWizardData] = useState(null);
  const [techWizardLoading, setTechWizardLoading] = useState(false);
  const [showColorConfig, setShowColorConfig] = useState(false);
  const [colorConfig, setColorConfig] = useState({ branches: [], zones: [] });
  const iso = toISODate(anchor);

  const fetchSummary = async (silent = false) => {
    // v93.2.0 · El refresco automático es silencioso (antes mostraba y ocultaba "Cargando…" cada 30 s)
    if (!silent) setLoading(true);
    try {
      const params = { date: iso };
      if (branchId) params.branch_id = branchId;
      if (zoneId) params.zone_id = zoneId;
      if (otSearch.trim()) params.q = otSearch.trim();
      if (statusFilter) params.status = statusFilter;
      if (techFilterGlobal) params.tech_id = techFilterGlobal;
      const r = await api.get("/v1/warroom/tech-summary", { params });
      setItems(r.data.items || []);
      setFleet(r.data.fleet || []);
      setFleetCounts(r.data.fleet_counts || {});
      // v3.9.7 · NO limpiar selección del técnico durante auto-refresh.
      // El usuario reportaba pérdida de contexto (selección + zoom) cada ciclo.
      // Solo limpiamos si el técnico desapareció por más de X ciclos consecutivos.
    } catch (e) {
      console.error(e);
      if (!silent) { toast.error("Error al cargar War Room"); setItems([]); }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  const fetchLayers = async () => {
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (zoneId) params.zone_id = zoneId;
      const { data } = await api.get("/v1/warroom/layers", { params });
      setLayers(data || { events: [], blocked_zones: [] });
    } catch { /* silent */ }
  };
  useEffect(() => {
    api.get("/v1/mesa/branches/tree").then(r => setBranches(r.data.tree || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchLayers(); /* eslint-disable-next-line */ }, [branchId, zoneId]);

  useEffect(() => { fetchSummary(); /* eslint-disable-next-line */ }, [iso, branchId, zoneId, otSearch, statusFilter, techFilterGlobal]);
  // v98.8.0 · GPS tracking cada 5 minutos (antes 30s)
  useEffect(() => {
    const t = setInterval(() => { fetchSummary(true); }, 300_000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [iso, branchId, zoneId, otSearch, statusFilter, techFilterGlobal]);

  // v98.8.0 · Fetch colores de sucursales/zonas al montar
  const fetchColors = async () => {
    try {
      const { data } = await api.get("/v1/warroom/cc/colors");
      setColorConfig(data);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchColors(); }, []);

  // v98.8.0 · Tech wizard: fetch KPIs cuando se abre
  useEffect(() => {
    if (!showTechWizard) { setTechWizardData(null); return; }
    setTechWizardLoading(true);
    api.get(`/v1/warroom/cc/tech-detail/${showTechWizard}`, { params: { date: iso } })
      .then(({ data }) => setTechWizardData(data))
      .catch(() => toast.error("Error al cargar detalle del técnico"))
      .finally(() => setTechWizardLoading(false));
  }, [showTechWizard, iso]);


  const selected = useMemo(
    () => items.find(x => x.tech_id === selectedTechId) || null,
    [items, selectedTechId]
  );

  const totalOts = items.reduce((a, b) => a + (b.ots_count || 0), 0);
  const totalKm = items.reduce((a, b) => a + (b.total_km || 0), 0);
  const avgOcc = (() => {
    const vals = items.map(x => x.occupation_pct).filter(v => v != null);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();
  const avgKmGroup = items.length > 0 ? totalKm / items.length : 0;

  // v10.2 · SLA Alerts: OTs en curso que superan 2h desde scheduled_at
  const slaAlerts = useMemo(() => {
    const alerts = [];
    for (const tech of items) {
      for (const stop of (tech.stops || [])) {
        if (!stop.scheduled_at) continue;
        const st = (stop.internal_status || "").toLowerCase();
        const isActive = !["finalizada","completada","completed","cerrada","closed","cancelada"].includes(st);
        if (!isActive) continue;
        const diff = Date.now() - new Date(stop.scheduled_at).getTime();
        const overMinutes = Math.floor(diff / 60000);
        if (overMinutes > 120) {
          const hrs = Math.floor(overMinutes / 60);
          const mins = overMinutes % 60;
          alerts.push({
            ...stop,
            tech_name: tech.tech_name,
            tech_id: tech.tech_id,
            overdue: `${hrs}h ${mins}m`,
            overMinutes,
          });
        }
      }
    }
    return alerts.sort((a, b) => b.overMinutes - a.overMinutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tick]);

  const points = items
    .filter(x => x.pin_lat != null && x.pin_lon != null)
    .map(x => ({ id: x.tech_id, lat: x.pin_lat, lon: x.pin_lon }));

  const routeCoords = selected?.stops
    ?.filter(s => s.lat != null && s.lon != null)
    .map(s => [s.lat, s.lon]) || [];

  const defaultCenter = [-0.9482, -80.7301]; // Manta (centro ops)

  // v9.0 · Collect all OT stops from all techs for individual pins
  // Colores estrictos: Rojo=Pendiente, Amarillo=En Curso, Verde=Finalizada
  const OT_STATUS_COLORS = {
    "nueva": "#DC2626",             // red - pendiente
    "pendiente": "#DC2626",         // red
    "pending": "#DC2626",           // red
    "asignada": "#DC2626",          // red - aun no empezada
    "assigned": "#DC2626",          // red
    "en_ejecucion": "#F59E0B",      // yellow/amber - en curso
    "on_site": "#F59E0B",           // yellow
    "in_progress": "#F59E0B",       // yellow
    "confirmada": "#F59E0B",        // yellow
    "finalizada": "#059669",        // green - completada
    "completada": "#059669",        // green
    "completed": "#059669",         // green
    "cerrada": "#059669",           // green
    "closed": "#059669",            // green
    "cancelada": "#6B7280",         // gray
  };
  const getOtColor = (status) => OT_STATUS_COLORS[(status || "").toLowerCase()] || "#DC2626";

  // Live tick para cronometro (actualiza cada 60s)
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const allOtStops = useMemo(() => {
    const stops = [];
    for (const tech of items) {
      for (const stop of (tech.stops || [])) {
        if (stop.lat != null && stop.lon != null) {
          const st = (stop.internal_status || "").toLowerCase();
          const isInProgress = st === "en_ejecucion" || st === "in_progress" || st === "on_site" || st === "confirmada";
          let elapsed = null;
          if (isInProgress && stop.scheduled_at) {
            const diff = Date.now() - new Date(stop.scheduled_at).getTime();
            if (diff > 0) {
              const mins = Math.floor(diff / 60000);
              const hrs = Math.floor(mins / 60);
              elapsed = hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
            }
          }
          stops.push({
            ...stop,
            tech_name: tech.tech_name,
            tech_id: tech.tech_id,
            color: getOtColor(stop.internal_status),
            isInProgress,
            elapsed,
          });
        }
      }
    }
    return stops;
  }, [items, tick]);

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0A3747]" data-testid="warroom-container">
      {/* Header: nav de día + KPIs */}
      <header className="flex-shrink-0 bg-[#0A3747] text-white border-b border-[#E0E621]/30 px-4 py-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/apps")}
            className="p-2 rounded hover:bg-white/10 transition"
            title="Volver"
            data-testid="warroom-back"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[.2em] text-[#E0E621]/80">War Room</span>
            <span className="text-sm font-bold">Unificado</span>
          </div>
        </div>

        <div className="flex items-center gap-1 ml-2">
          <button onClick={() => setAnchor(addDays(anchor, -1))} data-testid="warroom-prev-day"
                  className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition">
            <ChevronLeft size={14} />
          </button>
          <button onClick={() => setAnchor(new Date())} data-testid="warroom-today"
                  className="px-3 py-1 rounded bg-[#E0E621] text-[#0A3747] font-bold text-xs">
            HOY
          </button>
          <button onClick={() => setAnchor(addDays(anchor, 1))} data-testid="warroom-next-day"
                  className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition">
            <ChevronRight size={14} />
          </button>
          <div className="ml-3 text-xs font-semibold text-white/90 flex items-center gap-1.5">
            <CalIcon size={13} className="text-[#E0E621]" />
            {anchor.toLocaleDateString("es-EC", {
              weekday: "long", day: "numeric", month: "long", year: "numeric",
              timeZone: "America/Guayaquil",
            })}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 text-xs flex-wrap">
          {/* v9.0 · Filtros globales (Sucursal, Estado, Técnico, Búsqueda) */}
          <select
            value={branchId}
            onChange={(e) => { setFilter("branch_office_id", e.target.value); setFilter("zone_id", ""); }}
            className="text-[11px] bg-white/10 text-white border border-white/20 rounded px-2 py-1 outline-none"
            data-testid="warroom-branch-filter"
            title="Sucursal"
          >
            <option value="" style={{ color: "#000" }}>Todas las sucursales</option>
            {branches.map(b => (
              <option key={b.id} value={b.id} style={{ color: "#000" }}>{b.name}</option>
            ))}
          </select>
          <select
            value={zoneId}
            onChange={(e) => setFilter("zone_id", e.target.value)}
            className="text-[11px] bg-white/10 text-white border border-white/20 rounded px-2 py-1 outline-none"
            data-testid="warroom-zone-filter"
            title="Zona"
          >
            <option value="" style={{ color: "#000" }}>Todas las zonas</option>
            {(branches.find(b => String(b.id) === String(branchId))?.zones || []).map(z => (
              <option key={z.id} value={z.id} style={{ color: "#000" }}>{z.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setFilter("internal_status", e.target.value)}
            className="text-[11px] bg-white/10 text-white border border-white/20 rounded px-2 py-1 outline-none"
            data-testid="warroom-status-filter"
            title="Estado"
          >
            <option value="" style={{ color: "#000" }}>Todos los estados</option>
            <option value="nueva" style={{ color: "#000" }}>Nueva</option>
            <option value="asignada" style={{ color: "#000" }}>Asignada</option>
            <option value="en_ejecucion" style={{ color: "#000" }}>En Ejecución</option>
            <option value="finalizada" style={{ color: "#000" }}>Finalizada</option>
            <option value="cancelada" style={{ color: "#000" }}>Cancelada</option>
          </select>
          <div className="flex items-center gap-1 bg-white/10 rounded px-2 py-1">
            <SearchIcon size={11} className="text-white/70" />
            <input
              value={otSearch}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="OT #, cliente…"
              className="text-[11px] bg-transparent text-white placeholder-white/50 w-28 outline-none"
              data-testid="warroom-ot-search"
            />
            {otSearch && (
              <button onClick={() => setFilter("q", "")} className="text-white/60 hover:text-white" data-testid="warroom-ot-search-clear">
                <X size={10} />
              </button>
            )}
          </div>
          {/* Toggle layout 2x2 / mapa completo */}
          <div className="flex items-center gap-1 bg-white/10 rounded p-0.5">
            <button
              onClick={() => setLayoutMode("2x2")}
              className={`px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 ${layoutMode === "2x2" ? "bg-[#E0E621] text-[#0A3747]" : "text-white/80"}`}
              data-testid="warroom-layout-grid"
              title="Vista 2x2 (mapa + calendario + rutas + filtros)"
            >
              <LayoutGrid size={11} /> 2×2
            </button>
            <button
              onClick={() => setLayoutMode("map")}
              className={`px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 ${layoutMode === "map" ? "bg-[#E0E621] text-[#0A3747]" : "text-white/80"}`}
              data-testid="warroom-layout-map"
              title="Mapa a pantalla completa"
            >
              <Maximize2 size={11} /> Mapa
            </button>
            {/* v98.8.0 · Botón Colores */}
            <button
              onClick={() => setShowColorConfig(true)}
              className="px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 text-white/80 hover:bg-white/10"
              data-testid="warroom-btn-colors"
              title="Configurar colores por sucursal/zona"
            >
              <Palette size={11} /> Colores
            </button>
          </div>
          <div className="flex items-center gap-1.5" data-testid="warroom-kpi-techs">
            <Users size={13} className="text-[#E0E621]" />
            <span className="font-black">{items.length}</span>
            <span className="text-white/70">técnicos</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="warroom-kpi-ots">
            <MapPin size={13} className="text-[#E0E621]" />
            <span className="font-black">{totalOts}</span>
            <span className="text-white/70">OTs</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="warroom-kpi-km">
            <Milestone size={13} className="text-[#E0E621]" />
            <span className="font-black">{totalKm.toFixed(0)}</span>
            <span className="text-white/70">km</span>
            <span className="text-[9px] text-white/50">(avg {avgKmGroup.toFixed(1)})</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="warroom-kpi-occupation">
            <Gauge size={13} className="text-[#E0E621]" />
            <span className="font-black">{avgOcc == null ? "—" : `${avgOcc}%`}</span>
            <span className="text-white/70">ocup. prom.</span>
          </div>
          {slaAlerts.length > 0 && (
            <div className="flex items-center gap-1.5 bg-red-600/80 rounded px-2 py-0.5" data-testid="warroom-kpi-sla">
              <Clock size={13} className="text-white" />
              <span className="font-black text-white">{slaAlerts.length}</span>
              <span className="text-white/90 text-[10px]">SLA</span>
            </div>
          )}
          <button onClick={fetchSummary} data-testid="warroom-refresh"
                  className="p-2 rounded bg-white/10 hover:bg-white/20 transition"
                  title="Refrescar">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />}
          </button>
        </div>
      </header>

      {/* v3.6.0 · Body 2x2 · C1 Mapa | C2 Calendario · C3 Rutas | C4 Filtros+KPIs */}
      <div className="flex-1 relative min-h-0">
        {layoutMode === "2x2" ? (
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 bg-[#0A3747]" data-testid="warroom-quad-2x2">
            {/* C1 · Mapa con Smart Pins + Live tracking */}
            <div className="relative bg-white overflow-hidden" data-testid="warroom-quad-c1">
              <QuadBadge label="C1 · Mapa Táctico" />
              <MapContainer center={defaultCenter} zoom={7} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                <EventLayers layers={layers} />
                {points.length > 0 && !selectedTechId && <FitBoundsOnce points={points} />}
                {selected && selected.pin_lat != null && (
                  <FocusHandler target={{ lat: selected.pin_lat, lon: selected.pin_lon }} />
                )}
                {items.map((t) => t.pin_lat != null && t.pin_lon != null && (
                  <Marker
                    key={t.tech_id}
                    position={[t.pin_lat, t.pin_lon]}
                    icon={buildDynamicTechPin(t, t.tech_id === selectedTechId, t.branch_color)}
                    eventHandlers={{ click: () => { setSelectedTechId(t.tech_id); setShowTechWizard(t.tech_id); } }}
                  >
                    <Popup>
                      <div className="text-xs" data-testid={`warroom-pin-popup-${t.tech_id}`}>
                        <div className="font-bold text-[#0A3747]">{t.tech_name}</div>
                        <div className="text-gray-700 mt-1">
                          {t.ots_count} OTs · {t.done_count || 0} cerradas · {t.total_km.toFixed(1)} km
                        </div>
                        <div className="text-[10px] text-blue-600 mt-0.5">Click para ver detalle</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* v3.6.0 · Live tracking: círculo pulsante en la última posición GPS */}
                {/* v98.8.0 · Rutas: línea recta desde técnico a OT en progreso/en_camino con ETA */}
                {items.map(t => {
                  if (!t.pin_lat || !t.pin_lon) return null;
                  const activeOt = (t.stops || []).find(s => TRAVELING_STATES.includes((s.internal_status || "").toLowerCase()));
                  if (!activeOt || !activeOt.lat || !activeOt.lon) return null;
                  const dLat = (activeOt.lat - t.pin_lat) * Math.PI / 180;
                  const dLon = (activeOt.lon - t.pin_lon) * Math.PI / 180;
                  const a2 = Math.sin(dLat / 2) ** 2 + Math.cos(t.pin_lat * Math.PI / 180) * Math.cos(activeOt.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
                  const distKm = 6371 * 2 * Math.asin(Math.sqrt(a2));
                  const etaMin = Math.round(distKm / 30 * 60);
                  return (
                    <Polyline key={`route-${t.tech_id}`} positions={[[t.pin_lat, t.pin_lon], [activeOt.lat, activeOt.lon]]}
                      pathOptions={{ color: t.branch_color || "#3B82F6", weight: 3, dashArray: "8 6", opacity: 0.7 }}>
                      <Popup><div className="text-[10px]"><b>{t.tech_name}</b> → OT {activeOt.odoo_task_id}<br/>{distKm.toFixed(1)} km · ~{etaMin} min</div></Popup>
                    </Polyline>
                  );
                })}
                {/* v9.0 · OT Stop Pins coloreados por estado + elapsed time */}
                {showOts && allOtStops.map((ot) => (
                  <CircleMarker
                    key={`ot-${ot.odoo_task_id}`}
                    center={[ot.lat, ot.lon]}
                    radius={ot.isInProgress ? 8 : 6}
                    pathOptions={{
                      color: ot.color,
                      fillColor: ot.color,
                      fillOpacity: ot.isInProgress ? 0.9 : 0.6,
                      weight: ot.isInProgress ? 3 : 1.5,
                    }}
                  >
                    <Popup>
                      <div className="text-xs min-w-[160px]" data-testid={`warroom-ot-popup-${ot.odoo_task_id}`}>
                        <div className="font-bold text-[#0A3747]">{ot.task_number || `OT-${ot.odoo_task_id}`}</div>
                        <div className="text-gray-700">{ot.client_name}</div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: ot.color }}>
                            {ot.internal_status || "—"}
                          </span>
                          {ot.isInProgress && ot.elapsed && (
                            <span className="text-[10px] font-mono font-bold text-amber-600">
                              {ot.elapsed}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-500 mt-1 text-[10px]">{ot.tech_name}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
                {selected && routeCoords.length > 1 && (
                  <>
                    <Polyline positions={routeCoords} pathOptions={{ color: "#E0E621", weight: 4, opacity: 0.9 }} />
                    <DistanceMarkers stops={(selected?.stops || []).filter(s => s.lat && s.lon)} />
                  </>
                )}
              </MapContainer>
              <MapControls compact showTechs={showTechs} setShowTechs={setShowTechs}
                showOts={showOts} setShowOts={setShowOts} />
            </div>

            {/* C2 · Calendario adaptativo */}
            <div className="relative bg-white overflow-auto" data-testid="warroom-quad-c2">
              <QuadBadge label="C2 · Calendario del día" />
              <CalendarQuad items={items} selectedTechId={selectedTechId} onSelectTech={setSelectedTechId} />
            </div>

            {/* C3 · Rutas del técnico seleccionado */}
            <div className="relative bg-white overflow-auto" data-testid="warroom-quad-c3">
              <QuadBadge label="C3 · Rutas del técnico" />
              <RoutesQuad tech={selected} layers={layers} onOpenOT={(id) => navigate(`/ot-smart/${id}`)} />
            </div>

            {/* C4 · Centro de Mando: SLA + KMs + Acciones */}
            <div className="relative bg-white overflow-auto" data-testid="warroom-quad-c4">
              <QuadBadge label="C4 · Centro de Mando" />
              <CommandQuad
                items={items}
                slaAlerts={slaAlerts}
                avgKmGroup={avgKmGroup}
                techFilter={techFilter}
                onFilterChange={setTechFilter}
                selectedTechId={selectedTechId}
                onSelectTech={setSelectedTechId}
                onNavigate={navigate}
              />
            </div>
          </div>
        ) : (
        <>
        <div className="absolute inset-0" data-testid="warroom-map">
          <MapContainer center={defaultCenter} zoom={7} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                       attribution="&copy; OSM" />
            {points.length > 0 && !selectedTechId && <FitBoundsOnce points={points} />}
            {selected && selected.pin_lat != null && (
              <FocusHandler target={{ lat: selected.pin_lat, lon: selected.pin_lon }} />
            )}
            {items.map((t) => {
              if (t.pin_lat == null || t.pin_lon == null) return null;
              return (
                <Marker
                  key={t.tech_id}
                  position={[t.pin_lat, t.pin_lon]}
                  icon={buildDynamicTechPin(t, t.tech_id === selectedTechId, t.branch_color)}
                  eventHandlers={{ click: () => { setSelectedTechId(t.tech_id); setShowTechWizard(t.tech_id); } }}
                >
                  <Popup>
                    <div className="text-xs" data-testid={`warroom-pin-popup-${t.tech_id}`}>
                      <div className="font-bold text-[#0A3747]">{t.tech_name}</div>
                      <div className="text-gray-500">{t.tech_email}</div>
                      <div className="mt-1 text-gray-700">
                        {t.ots_count} OTs · {t.total_km.toFixed(1)} km
                      </div>
                      <button
                        onClick={() => setShowTechWizard(t.tech_id)}
                        data-testid={`warroom-open-tech-${t.tech_id}`}
                        className="mt-2 w-full px-2 py-1 rounded bg-[#0A3747] text-[#E0E621] font-bold text-[10px]"
                      >
                        Ver detalle
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {/* v98.8.0 · Rutas (mapa completo) */}
            {items.map(t => {
              if (!t.pin_lat || !t.pin_lon) return null;
              const activeOt = (t.stops || []).find(s => TRAVELING_STATES.includes((s.internal_status || "").toLowerCase()));
              if (!activeOt || !activeOt.lat || !activeOt.lon) return null;
              const dLat = (activeOt.lat - t.pin_lat) * Math.PI / 180;
              const dLon = (activeOt.lon - t.pin_lon) * Math.PI / 180;
              const a2 = Math.sin(dLat / 2) ** 2 + Math.cos(t.pin_lat * Math.PI / 180) * Math.cos(activeOt.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
              const distKm = 6371 * 2 * Math.asin(Math.sqrt(a2));
              const etaMin = Math.round(distKm / 30 * 60);
              return (
                <Polyline key={`route-full-${t.tech_id}`} positions={[[t.pin_lat, t.pin_lon], [activeOt.lat, activeOt.lon]]}
                  pathOptions={{ color: t.branch_color || "#3B82F6", weight: 3, dashArray: "8 6", opacity: 0.7 }}>
                  <Popup><div className="text-[10px]"><b>{t.tech_name}</b> → OT {activeOt.odoo_task_id}<br/>{distKm.toFixed(1)} km · ~{etaMin} min</div></Popup>
                </Polyline>
              );
            })}
            {/* v9.0 · OT Stop Pins coloreados por estado + elapsed time (mapa completo) */}
            {showOts && allOtStops.map((ot) => (
              <CircleMarker
                key={`ot-full-${ot.odoo_task_id}`}
                center={[ot.lat, ot.lon]}
                radius={ot.isInProgress ? 8 : 6}
                pathOptions={{
                  color: ot.color,
                  fillColor: ot.color,
                  fillOpacity: ot.isInProgress ? 0.9 : 0.6,
                  weight: ot.isInProgress ? 3 : 1.5,
                }}
              >
                <Popup>
                  <div className="text-xs min-w-[160px]">
                    <div className="font-bold text-[#0A3747]">{ot.task_number || `OT-${ot.odoo_task_id}`}</div>
                    <div className="text-gray-700">{ot.client_name}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: ot.color }}>
                        {ot.internal_status || "—"}
                      </span>
                      {ot.isInProgress && ot.elapsed && (
                        <span className="text-[10px] font-mono font-bold text-amber-600">{ot.elapsed}</span>
                      )}
                    </div>
                    <div className="text-gray-500 mt-1 text-[10px]">{ot.tech_name}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
            {selected && routeCoords.length > 1 && (
              <Polyline
                positions={routeCoords}
                pathOptions={{ color: "#E0E621", weight: 4, opacity: 0.9 }}
                data-testid="warroom-route-polyline"
              />
            )}
          </MapContainer>
        </div>

        {/* v93.3.0 · Capas + leyenda del mapa consolidado */}
        <MapControls showTechs={showTechs} setShowTechs={setShowTechs}
          showOts={showOts} setShowOts={setShowOts} />

        {loading && (
          <div className="absolute top-3 right-3 z-[500] bg-white/95 border border-gray-200 rounded-lg px-3 py-2 text-xs flex items-center gap-2 shadow-lg">
            <Loader2 size={13} className="animate-spin text-gray-500" />
            <span className="text-gray-700 font-semibold">Cargando War Room…</span>
          </div>
        )}

        {!loading && items.length === 0 && !fleet.some(f => f.last_ping) && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center pointer-events-none">
            <div className="bg-white/95 border border-gray-200 rounded-xl px-6 py-4 shadow-lg text-center">
              <MapPin size={24} className="mx-auto text-gray-400 mb-2" />
              <div className="text-sm font-bold text-[#0A3747]">Sin OTs agendadas hoy</div>
              <div className="text-xs text-gray-500 mt-1">Prueba con otra fecha o activa el Autopilot A2</div>
            </div>
          </div>
        )}

        {/* Sidebar deslizante · sólo en modo mapa (en 2x2 C3 muestra la ruta) */}
        <aside
          className={`absolute top-0 right-0 h-full bg-white border-l border-gray-200 shadow-2xl transition-transform duration-300 ${
            selected ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: "min(420px, 92vw)" }}
          data-testid="warroom-sidebar"
        >
          {selected && (
            <TechSidebar
              tech={selected}
              onClose={() => setSelectedTechId(null)}
              onOpenOT={(otid) => navigate(`/ot-smart/${otid}`)}
            />
          )}
        </aside>
        </>
        )}
      </div>

      {/* Leyenda inferior */}
      <div className="absolute bottom-3 left-3 z-[500] bg-white/95 border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-[10px] flex items-center gap-3">
        <span className="font-bold uppercase tracking-wider text-gray-500">Iconos</span>
        <span className="flex items-center gap-1">🚐 Viajando</span>
        <span className="flex items-center gap-1">👤 En sitio</span>
        <span className="ml-2 font-bold uppercase tracking-wider text-gray-500">Bloqueos</span>
        <LegendDot color="#DC2626" label="Zona" />
        <LegendDot color="#FACC15" label="Sucursal" />
        <LegendDot color="#39FF14" label="Técnico" />
      </div>

      {/* v98.8.0 · PANEL DE CONFIGURACIÓN DE COLORES */}
      {showColorConfig && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={() => setShowColorConfig(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[540px] max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()} data-testid="warroom-color-config">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-[#0A3747]">Colores por Sucursal / Zona</h3>
              <button onClick={() => setShowColorConfig(false)} className="text-gray-400 hover:text-gray-600 text-lg font-bold">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">Sucursales</div>
                {colorConfig.branches.map(b => (
                  <div key={b.id} className="flex items-center gap-3 py-1.5 border-b border-gray-100">
                    <input type="color" value={b.color || "#64748B"} onChange={async (e) => {
                      const c = e.target.value;
                      try {
                        await api.patch("/v1/warroom/cc/colors", { entity_type: "branch", entity_id: b.id, color: c });
                        setColorConfig(prev => ({ ...prev, branches: prev.branches.map(x => x.id === b.id ? { ...x, color: c } : x) }));
                        toast.success(`Color de ${b.name} actualizado`);
                      } catch { toast.error("Error al guardar color"); }
                    }} className="w-8 h-8 cursor-pointer rounded border" data-testid={`color-branch-${b.id}`} />
                    <span className="text-xs font-medium">{b.name}</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{b.color || "Sin color"}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">Zonas</div>
                {colorConfig.zones.map(z => (
                  <div key={z.id} className="flex items-center gap-3 py-1.5 border-b border-gray-100">
                    <input type="color" value={z.color || "#64748B"} onChange={async (e) => {
                      const c = e.target.value;
                      try {
                        await api.patch("/v1/warroom/cc/colors", { entity_type: "zone", entity_id: z.id, color: c });
                        setColorConfig(prev => ({ ...prev, zones: prev.zones.map(x => x.id === z.id ? { ...x, color: c } : x) }));
                        toast.success(`Color de ${z.name} actualizado`);
                      } catch { toast.error("Error al guardar color"); }
                    }} className="w-8 h-8 cursor-pointer rounded border" data-testid={`color-zone-${z.id}`} />
                    <span className="text-xs font-medium">{z.name}</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{z.color || "Sin color"}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-lg bg-gray-50 text-[10px] text-gray-500">
                <div className="font-bold mb-1">Colores reservados (no usar para sucursales/zonas):</div>
                <div className="flex gap-4">
                  <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ background: BLOCK_COLORS.polygon }} /> Bloqueo polígono</span>
                  <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ background: BLOCK_COLORS.branch }} /> Bloqueo sucursal</span>
                  <span><span className="inline-block w-3 h-3 rounded mr-1" style={{ background: BLOCK_COLORS.tech }} /> Bloqueo técnico</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* v98.8.0 · WIZARD DE TÉCNICO */}
      {showTechWizard && (
        <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/50" onClick={() => setShowTechWizard(null)}>
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full sm:w-[480px] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="warroom-tech-wizard">
            {techWizardLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Cargando...</div>
            ) : techWizardData ? (
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base font-bold text-[#0A3747]">{techWizardData.name}</div>
                    <div className="text-[10px] text-gray-500">{techWizardData.branch_name || "Sin sucursal"} · {techWizardData.date}</div>
                  </div>
                  <button onClick={() => setShowTechWizard(null)} className="text-gray-400 hover:text-gray-600 text-lg font-bold" data-testid="tech-wizard-close">×</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "KM Recorridos", value: `${techWizardData.km_today} km`, color: "#0EA5E9" },
                    { label: "OTs Total", value: techWizardData.ots_total, color: "#6366F1" },
                    { label: "Abiertas", value: techWizardData.ots_open, color: "#F59E0B" },
                    { label: "En Progreso", value: techWizardData.ots_in_progress, color: "#8B5CF6" },
                    { label: "Cerradas", value: techWizardData.ots_done, color: "#16A34A" },
                    { label: "Prom. Atención", value: techWizardData.avg_attention_min ? `${techWizardData.avg_attention_min} min` : "—", color: "#0A3747" },
                  ].map(kpi => (
                    <div key={kpi.label} className="p-3 rounded-lg" style={{ background: kpi.color + "10", borderLeft: `3px solid ${kpi.color}` }}>
                      <div className="text-[10px] font-medium text-gray-500">{kpi.label}</div>
                      <div className="text-lg font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 p-3 rounded-lg bg-gray-50 text-center">
                    <div className="text-[10px] text-gray-500">Prom. Recorrido entre OTs</div>
                    <div className="text-sm font-bold text-[#0A3747]">{techWizardData.avg_travel_min ? `${techWizardData.avg_travel_min} min` : "—"}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">Órdenes del día</div>
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {(techWizardData.ots || []).map(o => {
                      const st = (o.internal_status || "").toLowerCase();
                      const isDone = ["cerrada", "completada", "done", "completed", "validada"].includes(st);
                      const isActive = ["en_ejecucion", "in_progress", "en_sitio", "on_site", "confirmada"].includes(st);
                      return (
                        <div key={o.odoo_task_id} className="flex items-center gap-2 p-2 rounded-lg border text-[11px]" style={{
                          background: isDone ? "#F0FDF4" : isActive ? "#EFF6FF" : "#FFFBEB",
                          borderColor: isDone ? "#BBF7D0" : isActive ? "#BFDBFE" : "#FDE68A",
                        }}>
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: isDone ? "#16A34A" : isActive ? "#3B82F6" : "#F59E0B" }} />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold truncate">{o.number || o.odoo_task_id} — {o.client_name || "Sin cliente"}</div>
                            <div className="text-[9px] text-gray-500">{o.internal_status}</div>
                          </div>
                        </div>
                      );
                    })}
                    {(techWizardData.ots || []).length === 0 && (
                      <div className="text-[11px] text-gray-400 text-center py-4">Sin OTs para este día</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Sin datos</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className="w-2.5 h-2.5 rounded-full border border-white shadow" style={{ background: color }} />
      <span className="text-gray-700">{label}</span>
    </span>
  );
}

function TechSidebar({ tech, onClose, onOpenOT }) {
  return (
    <div className="h-full flex flex-col" data-testid={`warroom-sidebar-content-${tech.tech_id}`}>
      <div className="flex-shrink-0 px-4 py-3 bg-[#0A3747] text-white flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[.2em] text-[#E0E621]/80">Técnico</div>
          <div className="text-sm font-bold truncate" data-testid="warroom-tech-name">{tech.tech_name}</div>
          <div className="text-[10px] text-white/70 truncate">{tech.tech_email}</div>
        </div>
        <button onClick={onClose} data-testid="warroom-sidebar-close"
                className="p-1.5 rounded hover:bg-white/10 transition ml-2">
          <X size={16} />
        </button>
      </div>

      {/* KPIs */}
      <div className="flex-shrink-0 grid grid-cols-3 gap-2 px-3 py-3 border-b border-gray-200 bg-gray-50">
        <KpiBox label="OTs día" value={tech.ots_count} testid="warroom-kpi-tech-ots" />
        <KpiBox label="KM total" value={tech.total_km.toFixed(1)} testid="warroom-kpi-tech-km" />
        <KpiBox
          label="Ocupación"
          value={tech.occupation_pct == null ? "—" : `${Math.round(tech.occupation_pct)}%`}
          testid="warroom-kpi-tech-occupation"
          highlight
        />
      </div>

      {/* Mini-calendario cronológico */}
      <div className="flex-1 overflow-auto px-3 py-3" data-testid="warroom-mini-calendar">
        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2 flex items-center gap-1.5">
          <RouteIcon size={12} /> Recorrido del día ({tech.stops.length} paradas)
        </div>
        {tech.stops.length === 0 && (
          <div className="text-xs text-gray-400 italic">Sin OTs agendadas</div>
        )}
        <ol className="space-y-2">
          {tech.stops.map((s) => (
            <li
              key={s.odoo_task_id}
              className="border border-gray-200 rounded-lg p-2 hover:border-[#0A3747] cursor-pointer transition"
              onClick={() => onOpenOT(s.odoo_task_id)}
              data-testid={`warroom-stop-${s.odoo_task_id}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0A3747] text-[#E0E621] text-[10px] font-black flex items-center justify-center">
                  {s.done ? <CheckCircle2 size={13} className="text-emerald-400" /> : s.seq}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold truncate ${s.done ? "text-emerald-700 line-through" : "text-[#0A3747]"}`}>
                      OT #{s.task_number || s.odoo_task_id}
                    </span>
                    <span className="text-[10px] text-gray-500 flex items-center gap-0.5 flex-shrink-0">
                      <Clock size={10} /> {fmtTime(s.scheduled_at)}
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-700 truncate">{s.client_name}</div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {s.plan_name || s.activity_type || ""}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Acciones */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200 bg-gray-50 grid grid-cols-2 gap-2">
        <button
          disabled={tech.stops.length === 0}
          onClick={() => {
            const [first] = tech.stops;
            if (first && first.lat != null) {
              window.open(`https://www.google.com/maps/dir/?api=1&destination=${first.lat},${first.lon}`, "_blank");
            }
          }}
          className="px-3 py-1.5 rounded bg-[#0A3747] text-[#E0E621] text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="warroom-sidebar-open-first-nav"
        >
          <NavIcon size={11} className="inline mr-1" /> Abrir 1ª parada
        </button>
        <button
          onClick={() => onOpenOT(tech.stops[0]?.odoo_task_id)}
          disabled={tech.stops.length === 0}
          className="px-3 py-1.5 rounded bg-[#E0E621] text-[#0A3747] text-[11px] font-bold disabled:opacity-40"
          data-testid="warroom-sidebar-open-first-ot"
        >
          Ver OT
        </button>
      </div>
    </div>
  );
}

function KpiBox({ label, value, testid, highlight }) {
  return (
    <div className={`rounded-lg p-2 text-center ${highlight ? "bg-[#0A3747] text-white" : "bg-white border border-gray-200"}`}>
      <div className={`text-[9px] uppercase tracking-wider font-bold ${highlight ? "text-[#E0E621]" : "text-gray-500"}`}>
        {label}
      </div>
      <div className={`text-base font-black ${highlight ? "text-[#E0E621]" : "text-[#0A3747]"}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" });
  } catch { return "—"; }
}

// v3.6.2 · Renderiza polígonos de eventos activos (rojo = bloqueo, ámbar = calamidad)
function EventLayers({ layers }) {
  const events = layers?.events || [];
  return (
    <>
      {events.map((ev) => {
        const geo = ev.polygon_geojson;
        if (!geo || geo.type !== "Polygon" || !geo.coordinates?.length) return null;
        // GeoJSON viene [lng,lat]; Leaflet Polygon espera [lat,lng]
        const outer = geo.coordinates[0].map((p) => [p[1], p[0]]);
        const color = ev.category === "corte_via" || ev.category === "corte_fibra"
          ? "#DC2626" : ev.category === "clima" ? "#0891B2" : "#F59E0B";
        return (
          <Polygon
            key={ev.id}
            positions={outer}
            pathOptions={{ color, weight: 2, fillOpacity: 0.22, dashArray: "6 4" }}
          >
            <Popup>
              <div className="text-xs" data-testid={`warroom-layer-event-${ev.id}`}>
                <div className="font-bold text-red-700">🚨 {ev.title}</div>
                <div className="text-gray-600 mt-0.5">{ev.category}</div>
                <div className="text-[10px] text-gray-500">{ev.affected_ot_count} OTs afectadas</div>
              </div>
            </Popup>
          </Polygon>
        );
      })}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.6.0 · Cuadrantes 2x2 (C1 ya está inline en el body como MapContainer)
// ═══════════════════════════════════════════════════════════════════
function QuadBadge({ label }) {
  return (
    <div className="absolute top-2 left-2 z-[500] bg-[#0A3747]/90 text-[#E0E621] text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded pointer-events-none">
      {label}
    </div>
  );
}

function CalendarQuad({ items, selectedTechId, onSelectTech }) {
  // Timeline por técnico: barra 7:00–19:00 con bloques
  const START_H = 6, END_H = 20, SPAN = END_H - START_H;
  return (
    <div className="p-3 pt-8 h-full" data-testid="warroom-calendar-quad">
      {items.length === 0 && <div className="text-xs text-gray-400 italic">Sin técnicos con OTs</div>}
      <div className="space-y-1.5">
        {/* Escala horaria */}
        <div className="flex text-[9px] text-gray-400 border-b border-gray-200 pb-1">
          <div className="w-24 flex-shrink-0" />
          <div className="flex-1 relative">
            {Array.from({length: SPAN+1}).map((_, i) => (
              <span key={i} className="absolute" style={{ left: `${(i/SPAN)*100}%`, transform: "translateX(-50%)" }}>
                {String(START_H + i).padStart(2, "0")}
              </span>
            ))}
          </div>
        </div>
        {items.slice(0, 20).map(t => (
          <button
            key={t.tech_id}
            onClick={() => onSelectTech(t.tech_id === selectedTechId ? null : t.tech_id)}
            className={`w-full flex items-center gap-1 py-1 rounded ${t.tech_id === selectedTechId ? "bg-[#FEF3C7]" : "hover:bg-gray-50"}`}
            data-testid={`warroom-cal-row-${t.tech_id}`}
          >
            <div className="w-24 flex-shrink-0 text-left px-1">
              <div className="text-[10px] font-bold text-[#0A3747] truncate">{t.tech_name}</div>
              <div className="text-[9px] text-gray-500">{t.ots_count} OTs · {t.done_count || 0} ✓</div>
            </div>
            <div className="flex-1 relative h-5 bg-gray-100 rounded overflow-hidden">
              {t.stops.map((s, i) => {
                if (!s.scheduled_at) return null;
                const d = new Date(s.scheduled_at);
                const localH = d.getUTCHours() - 5 + d.getUTCMinutes()/60;
                const e = s.scheduled_end_at ? new Date(s.scheduled_end_at) : new Date(d.getTime() + 60*60*1000);
                const localE = e.getUTCHours() - 5 + e.getUTCMinutes()/60;
                const l = Math.max(0, ((localH - START_H) / SPAN) * 100);
                const w = Math.max(1, ((localE - localH) / SPAN) * 100);
                const bg = s.done ? "#059669" : "#0EA5E9";
                return (
                  <div key={i} className="absolute top-0 bottom-0 rounded" style={{ left: `${l}%`, width: `${w}%`, background: bg, opacity: 0.85 }} title={`${s.client_name} · ${fmtTime(s.scheduled_at)}`}>
                    {s.done && <CheckCircle2 size={10} className="text-white absolute top-1/2 -translate-y-1/2 left-1" />}
                  </div>
                );
              })}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function RoutesQuad({ tech, layers, onOpenOT }) {
  if (!tech) return (
    <div className="p-4 pt-8 text-xs text-gray-400 italic h-full flex items-center justify-center text-center" data-testid="warroom-routes-quad-empty">
      Selecciona un técnico en el mapa (C1) o el calendario (C2) para ver su ruta trazada.
    </div>
  );
  const stopsWithCoords = tech.stops.filter(s => s.lat != null && s.lon != null);
  const polyline = stopsWithCoords.map(s => [s.lat, s.lon]);
  return (
    <div className="relative h-full w-full" data-testid="warroom-routes-quad">
      {stopsWithCoords.length === 0 ? (
        <div className="p-4 pt-8 text-xs text-gray-400 italic h-full flex items-center justify-center">
          Sin coordenadas GPS en las paradas de este técnico.
        </div>
      ) : (
        <MapContainer
          key={`route-${tech.tech_id}`}
          bounds={stopsWithCoords.length > 1 ? polyline : undefined}
          center={stopsWithCoords.length === 1 ? polyline[0] : undefined}
          zoom={stopsWithCoords.length === 1 ? 13 : undefined}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
          <EventLayers layers={layers} />
          <RouteFit points={polyline} />
          {/* Polyline */}
          {polyline.length > 1 && (
            <Polyline
              positions={polyline}
              pathOptions={{ color: "#E0E621", weight: 5, opacity: 0.95 }}
              data-testid="warroom-c3-polyline"
            />
          )}
          <DistanceMarkers stops={stopsWithCoords} />
          {/* Markers numerados */}
          {stopsWithCoords.map((s, i) => (
            <Marker
              key={s.odoo_task_id}
              position={[s.lat, s.lon]}
              icon={buildNumberedPin(i + 1, s.done)}
            >
              <Popup>
                <div className="text-xs" data-testid={`warroom-c3-popup-${s.odoo_task_id}`}>
                  <div className="font-bold text-[#0A3747] flex items-center gap-1">
                    {s.done && <CheckCircle2 size={12} className="text-emerald-600" />}
                    Parada #{i + 1}
                  </div>
                  <div className={`mt-0.5 ${s.done ? "line-through text-gray-500" : "text-gray-700"}`}>
                    {s.client_name}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    OT #{s.task_number || s.odoo_task_id} · {fmtTime(s.scheduled_at)}
                  </div>
                  <button
                    onClick={() => onOpenOT(s.odoo_task_id)}
                    data-testid={`warroom-c3-open-${s.odoo_task_id}`}
                    className="mt-1.5 w-full px-2 py-1 rounded bg-[#0A3747] text-[#E0E621] font-bold text-[10px]"
                  >
                    Ver OT
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}

      {/* Resumen overlay */}
      <div className="absolute top-8 right-2 z-[500] bg-white/95 border border-gray-200 rounded shadow px-2 py-1.5 text-[10px] pointer-events-none" data-testid="warroom-c3-summary">
        <div className="font-black text-[#0A3747]">{tech.tech_name}</div>
        <div className="text-gray-700">
          {stopsWithCoords.length} paradas · <span className="font-bold">{tech.total_km.toFixed(1)} km</span>
          {tech.done_count > 0 && <span className="text-emerald-700 font-bold"> · {tech.done_count} ✓</span>}
        </div>
      </div>
    </div>
  );
}

// v3.6.1 · Pin numerado para C3 (verde si done, ámbar si pendiente)
function buildNumberedPin(seq, done) {
  const color = done ? "#059669" : "#0A3747";
  const label = done ? "✓" : String(seq);
  return L.divIcon({
    className: "",
    html: `<div style="
      width:28px;height:28px;background:${color};color:#E0E621;
      border-radius:50% 50% 50% 0;border:2px solid #fff;transform:rotate(-45deg);
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 3px 6px rgba(0,0,0,.35);
    "><span style="transform:rotate(45deg);font-weight:900;font-size:12px;font-family:system-ui,sans-serif">${label}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
    popupAnchor: [0, -22],
  });
}

// Ajusta el mapa al bounds de la ruta (cada vez que cambia el técnico)
function RouteFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 15 });
  }, [points, map]);
  return null;
}

// v10.2 · Haversine distance (km) entre 2 coords [lat,lon]
function haversineKm(a, b) {
  const R = 6371;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLon = (b[1] - a[1]) * Math.PI / 180;
  const lat1 = a[0] * Math.PI / 180;
  const lat2 = b[0] * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// v10.2 · Distance markers entre stops consecutivos
function DistanceMarkers({ stops }) {
  if (!stops || stops.length < 2) return null;
  return stops.slice(0, -1).map((s, i) => {
    const next = stops[i + 1];
    if (!s.lat || !next.lat) return null;
    const dist = haversineKm([s.lat, s.lon], [next.lat, next.lon]);
    if (dist < 0.05) return null;
    const midLat = (s.lat + next.lat) / 2;
    const midLon = (s.lon + next.lon) / 2;
    return (
      <CircleMarker
        key={`dist-${i}`}
        center={[midLat, midLon]}
        radius={0}
        pathOptions={{ opacity: 0 }}
      >
        <Popup autoClose={false} closeButton={false} className="leaflet-popup-dist">
          <span className="text-[9px] font-bold text-gray-700">{dist.toFixed(1)} km</span>
        </Popup>
      </CircleMarker>
    );
  });
}

function CommandQuad({ items, slaAlerts, avgKmGroup, techFilter, onFilterChange, selectedTechId, onSelectTech, onNavigate }) {
  const [tab, setTab] = useState("techs"); // techs | sla | actions
  const q = (techFilter || "").toLowerCase().trim();
  const filtered = q
    ? items.filter(t => (t.tech_name || "").toLowerCase().includes(q) || (t.tech_email || "").toLowerCase().includes(q))
    : items;
  const sortedByKm = [...items].sort((a, b) => (b.total_km || 0) - (a.total_km || 0));

  return (
    <div className="p-2 pt-8 h-full flex flex-col" data-testid="warroom-command-quad">
      {/* Tabs */}
      <div className="flex items-center gap-0.5 mb-2 border-b border-gray-200 pb-1.5 flex-shrink-0">
        {[
          { key: "techs", label: `Técnicos (${filtered.length})`, icon: Users },
          { key: "sla", label: `Alertas SLA${slaAlerts.length ? ` (${slaAlerts.length})` : ""}`, icon: Clock },
          { key: "actions", label: "Acciones", icon: Gauge },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1 px-2 py-1 rounded-t text-[10px] font-bold transition ${tab === key ? "bg-[#0A3747] text-[#E0E621]" : "text-gray-500 hover:text-[#0A3747] hover:bg-gray-100"}`}
            data-testid={`warroom-c4-tab-${key}`}
          >
            <Icon size={10} /> {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {tab === "techs" && (
          <div data-testid="warroom-c4-techs">
            <div className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 mb-2">
              <SearchIcon size={11} className="text-gray-400" />
              <input value={techFilter} onChange={(e) => onFilterChange(e.target.value)} placeholder="Filtrar..." className="flex-1 text-[11px] outline-none bg-transparent" data-testid="warroom-tech-filter" />
            </div>
            {/* KM Individual vs Promedio */}
            <div className="mb-2 bg-gradient-to-r from-[#0A3747] to-[#0A3747]/80 rounded-lg p-2" data-testid="warroom-km-comparison">
              <div className="text-[9px] uppercase tracking-widest font-bold text-[#E0E621] mb-1.5">KM Recorridos · Individual vs Grupo</div>
              <div className="space-y-1">
                {sortedByKm.slice(0, 8).map((t) => {
                  const pct = avgKmGroup > 0 ? (t.total_km / avgKmGroup) * 100 : 0;
                  const overAvg = pct > 100;
                  return (
                    <button key={t.tech_id} onClick={() => onSelectTech(t.tech_id)} className="w-full flex items-center gap-1.5 text-left" data-testid={`warroom-km-row-${t.tech_id}`}>
                      <div className="w-16 truncate text-[9px] text-white/80 font-medium">{(t.tech_name || "").split(" ")[0]}</div>
                      <div className="flex-1 relative h-3 bg-white/10 rounded overflow-hidden">
                        <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.min(pct, 100)}%`, background: overAvg ? "#059669" : "#F59E0B" }} />
                        {/* avg line */}
                        <div className="absolute top-0 bottom-0 w-px bg-[#E0E621]" style={{ left: `${Math.min(100, (avgKmGroup / (sortedByKm[0]?.total_km || 1)) * 100)}%` }} />
                      </div>
                      <div className="w-14 text-right text-[9px] font-black text-[#E0E621]">{t.total_km.toFixed(1)} km</div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[8px] text-white/60">
                <span className="flex items-center gap-0.5"><span className="w-2 h-1 bg-[#E0E621] rounded" /> Promedio: {avgKmGroup.toFixed(1)} km</span>
                <span className="flex items-center gap-0.5"><span className="w-2 h-1 bg-emerald-500 rounded" /> Sobre promedio</span>
                <span className="flex items-center gap-0.5"><span className="w-2 h-1 bg-amber-500 rounded" /> Bajo promedio</span>
              </div>
            </div>
            {/* Tech list */}
            <div className="space-y-0.5">
              {filtered.map(t => {
                const pct = t.occupation_pct;
                const color = pct == null ? "#64748B" : pct >= 90 ? "#B91C1C" : pct >= 70 ? "#D97706" : pct >= 40 ? "#059669" : "#0EA5E9";
                const kmDiff = t.total_km - avgKmGroup;
                return (
                  <button key={t.tech_id} onClick={() => onSelectTech(t.tech_id === selectedTechId ? null : t.tech_id)}
                    className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${t.tech_id === selectedTechId ? "bg-[#FEF3C7]" : "hover:bg-gray-50"}`}
                    data-testid={`warroom-filters-tech-${t.tech_id}`}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-[#0A3747] truncate">{t.tech_name}</div>
                      <div className="text-[9px] text-gray-500">
                        {t.ots_count} OTs · {t.done_count || 0} done · {t.total_km.toFixed(1)} km
                        <span className={`ml-1 font-bold ${kmDiff >= 0 ? "text-emerald-600" : "text-amber-600"}`}>
                          ({kmDiff >= 0 ? "+" : ""}{kmDiff.toFixed(1)})
                        </span>
                      </div>
                    </div>
                    <span title={gpsLabel(t.last_ping)} className="flex items-center gap-0.5 text-[9px] font-bold"
                      style={{ color: gpsColor(t.last_ping) }}>
                      <Radio size={10} />{t.last_ping ? "" : "Sin GPS"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "sla" && (
          <div data-testid="warroom-c4-sla">
            {slaAlerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-2" />
                <div className="text-sm font-bold text-emerald-700">Sin alertas SLA</div>
                <div className="text-xs text-gray-500 mt-1">Todas las OTs dentro de tiempo</div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-[10px] text-red-600 font-bold uppercase tracking-wider mb-1">
                  {slaAlerts.length} OTs excedieron el SLA (&gt;2h)
                </div>
                {slaAlerts.map((a) => (
                  <div key={a.odoo_task_id} className="border border-red-200 bg-red-50 rounded-lg p-2" data-testid={`warroom-sla-${a.odoo_task_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-red-800">OT #{a.task_number || a.odoo_task_id}</div>
                      <div className="text-[10px] font-mono font-black text-red-600 bg-red-100 px-1.5 py-0.5 rounded">{a.overdue}</div>
                    </div>
                    <div className="text-[10px] text-red-700 mt-0.5">{a.client_name}</div>
                    <div className="text-[9px] text-red-500 flex items-center gap-1 mt-0.5">
                      <Users size={9} /> {a.tech_name} · {a.internal_status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "actions" && (
          <div className="space-y-3 py-2" data-testid="warroom-c4-actions">
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Acciones Rápidas</div>
            <button
              onClick={() => onNavigate("/admin/calamidades-a5")}
              className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-red-200 bg-red-50 hover:bg-red-100 transition text-left"
              data-testid="warroom-action-calamities"
            >
              <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center flex-shrink-0">
                <Clock size={18} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-red-800">Gestionar Calamidades</div>
                <div className="text-[10px] text-red-600">Bloqueo de zonas por eventos externos</div>
              </div>
            </button>
            <button
              onClick={() => onNavigate("/admin/eventos-masivos")}
              className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 transition text-left"
              data-testid="warroom-action-mass-block"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center flex-shrink-0">
                <Gauge size={18} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-amber-800">Bloqueos Masivos</div>
                <div className="text-[10px] text-amber-600">Bloquear/desbloquear OTs por zona</div>
              </div>
            </button>
            <button
              onClick={() => onNavigate("/admin/a2-dryrun")}
              className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 transition text-left"
              data-testid="warroom-action-autopilot"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                <RouteIcon size={18} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-purple-800">Ejecutar Autopilot A2</div>
                <div className="text-[10px] text-purple-600">Simular y aplicar agendamiento automático</div>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
